import { defu } from "defu";
import type { AsyncDataOptions } from "#app";

export type BatchFetchConfig = {
  queryKey?: string;
};

export interface BatchUseFetchConfig<T> extends AsyncDataOptions<T> {
  queryKey?: string;
}

type FlushTimeout = ReturnType<typeof setTimeout> | null;

interface QueueItem<T> {
  id: unknown;
  idKey: string;
  resolve: ( val: T )=> void;
  reject: ( err?: any )=> void;
}

interface EndpointQueue<T = unknown> {
  queryKey: string; // TODO: could be config object
  flushTimeout: FlushTimeout;
  items: QueueItem<T>[];
}

type EndpointMap = Map<string, EndpointQueue>;

function serializeId ( id: unknown ): string {
  if ( typeof id === "string" || typeof id === "number" ) {
    return String( id );
  }

  if ( Array.isArray( id ) ) {
    return JSON.stringify( id.sort() );
  }

  if ( typeof id === "object" ) {
    // This won't order nested objects within the id object, but we shouldn't really be using
    // objects with nested objects as IDs anyway
    return JSON.stringify( id, Object.keys( id as object ).sort() );
  }

  return JSON.stringify( id );
}

export const useBatcherStore = defineStore( "batcher", () => {
  const endpointMap = ref<EndpointMap>( new Map<string, EndpointQueue>() );
  const dependencies = useFetchDependencies();

  async function flushQueue ( endpoint: string ) {
    const queue = endpointMap.value.get( endpoint );
    if ( !queue ) {
      console.warn( `Could not find queue for endpoint: ${endpoint}` );
      return;
    }
    endpointMap.value.delete( endpoint );

    const { queryKey, items } = queue;
    if ( !items.length ) {
      console.warn( `No items to flush for endpoint ${endpoint}` );
      return;
    }

    const serializedIds = [...new Set( items.map( i => i.idKey ) )].sort();

    try {
      const data = await $apiFetch<Record<string, unknown>>( endpoint, {
        query: {
          [queryKey]: serializedIds,
        },
      }, dependencies );

      for ( const { idKey, resolve, reject } of items ) {
        if ( data && data[idKey] !== undefined ) {
          resolve( data[idKey] as never );
        } else {
          reject( new Error( `No data found for ${idKey} in ${endpoint}` ) );
        }
      }
    } catch ( err ) {
      for ( const { reject } of items ) {
        reject( err );
      }
    }
  }

  function $fetch<T> ( endpoint: string, rawId: unknown, config: BatchFetchConfig = {} ): Promise<T> {
    const _config = defu( config, {
      queryKey: "ids",
    } );

    if ( !endpointMap.value.has( endpoint ) ) {
      endpointMap.value.set( endpoint, {
        queryKey: _config.queryKey,
        items: [],
        flushTimeout: null,
      } );
    }

    const queue = endpointMap.value.get( endpoint ) as EndpointQueue<T>;
    const idKey = serializeId( rawId );

    return new Promise<T>( ( resolve, reject ) => {
      queue.items.push( { id: rawId, idKey, resolve, reject } );

      if ( !queue.flushTimeout ) {
        queue.flushTimeout = setTimeout( async () => {
          queue.flushTimeout = null;
          await flushQueue( endpoint );
        }, 0 );
      }
    } );
  }

  function useFetch<T> ( endpoint: string, rawId: unknown, config: BatchUseFetchConfig<T> = {} ) {
    const _config = defu( config, {
      dedupe: "defer",
      queryKey: "ids",
      lazy: false,
    } as BatchUseFetchConfig<T> );

    // This line extracts the query key property from the _config object, so the rest of the properties
    // can be passed into useAsyncData with correct typing
    const { queryKey, ...asyncDataOptions } = _config;

    const id = serializeId( rawId );

    return useAsyncData( `${endpoint}-${id}`, () => $fetch<T>( endpoint, rawId, { queryKey } ), asyncDataOptions );
  }

  return {
    $fetch,
    useFetch,
  };
} );
